import { useState } from "react";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import accountApi from "../../api/accountApi";
import { Edit, Plus } from "lucide-react";
import BankInfoClient from "../../components/clients/BankInfoClient";
import bankApi from "../../api/bankApi";
import { Button } from "../../components/Button";
import { AiFillBank } from "react-icons/ai";

const BankDetails = ({ selectedClientData, onRefreshAccounts }) => {
  const bank = selectedClientData?.bankDetails;

  const token = useSelector((state) => state.auth.access_token);
  const [isUpdateBasic, setIsUpdateBasic] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState({});

  
  const safeTrim = (value) => (value || "").trim();

  const validateBankDetailsSimple = (isUpdateBasic = {}) => {
    const errors = {};

    const hasAnyValue = Object.values(isUpdateBasic).some((v) => v?.trim());

    if (!hasAnyValue) {
      return { valid: true, errors: {} };
    }

    if (!isUpdateBasic.bankName?.trim()) {
      errors.bankName = "Bank name is required";
    } else if (isUpdateBasic.bankName.trim().length < 3) {
      errors.bankName = "Bank name must be at least 3 characters";
    }

    if (!isUpdateBasic.accountNumber?.trim()) {
      errors.accountNumber = "Account number is required";
    } else if (!/^[0-9]{9,18}$/.test(isUpdateBasic.accountNumber)) {
      errors.accountNumber = "Account number must be 9–18 digits";
    }

    if (!isUpdateBasic.ifscCode?.trim()) {
      errors.ifscCode = "IFSC code is required";
    } else if (isUpdateBasic.ifscCode.length !== 11) {
      errors.ifscCode = "IFSC code must be exactly 11 characters";
    } else if (!/^[A-Z]{4}0[A-Z0-9]{6}$/.test(isUpdateBasic.ifscCode)) {
      errors.ifscCode = "Invalid IFSC format (e.g. SBIN0001234)";
    }

    return {
      valid: Object.keys(errors).length === 0,
      errors,
    };
  };

  const handelUpdate = async (e) => {
    e.preventDefault();
    const bankValidation = validateBankDetailsSimple(isUpdateBasic);
    setErrors(bankValidation.errors);

    if (!bankValidation.valid) {
      toast.error("Please fix bank details");
      return;
    }
    try {
      setIsLoading(true);
      await bankApi.updateBankDetails(isUpdateBasic?.id, isUpdateBasic, token);
      toast.success("Bank Details Update Successfully!");
      setIsUpdateBasic(null);
      onRefreshAccounts();
      setIsLoading(false);
    } catch (error) {
      console.error("Error fetching account:", error);
      setIsLoading(false);
    } finally {
      setIsLoading(false);
    }
  };

  // if (!bank) {
  //   return (
  //     <div className="flex flex-col items-center justify-center border border-dashed border-gray-300 rounded-lg py-8 bg-gray-50">
  //       <p className="text-sm font-medium text-gray-600 mb-3">
  //         No bank details available
  //       </p>
  //     </div>
  //   );
  // }

  return (
    <div className="relative bg-white p-1 border border-gray-100 shadow-sm rounded-lg overflow-hidden">
      {selectedClientData?.bankDetails === null ? 
            <div className="h-full p-1">
                <div className="flex items-center justify-between">
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">Bank Details</h3>
                    <button
                        type="button"
                        onClick={() =>
                          setIsUpdateBasic({
                            bankName: bank?.bankName ?? "",
                            accountNumber: bank?.accountNumber ?? "",
                            ifscCode: bank?.ifscCode ?? "",
                            swiftCode: bank?.swiftCode ?? "",
                            branchAddress: bank?.branchAddress ?? "",
                            id: selectedClientData?.id ?? "",
                          })
                        }
                        className="bg-primary text-white px-3 py-1 rounded-lg hover:bg-blue-700 transition-colors flex items-center justify-center space-x-2 w-full sm:w-auto"
                    >
                        <Plus className="h-4 w-4" />
                        <span>Bank Details</span>
                    </button>
                </div> 
                <div className="bg-white rounded-lg mt-[2%] shadow-sm border border-gray-200 p-6 text-center">
                                    <AiFillBank className="h-12 w-12 text-gray-500 mx-auto mb-4" />
                                    <h3 className="text-lg font-medium text-gray-900 mb-2">
                                        No Bank Details
                                    </h3>
                                    <p className="text-gray-600 mb-4">
                                        This client doesn't have any Bank Details yet.
                                    </p>
                                </div>

            </div> :
      <div className={`grid grid-cols-1 md:grid-cols-2 ${bank?.swiftCode ? "lg:grid-cols-3" : "lg:grid-cols-4"} gap-1`}>
            <DetailBlock label="Bank Name" value={bank?.bankName} />
            <DetailBlock label="Account Number" value={bank?.accountNumber} />
            <DetailBlock label="IFSC Code" value={bank?.ifscCode} />
            {bank?.swiftCode ? 
            <DetailBlock label="SWIFT Code" value={bank?.swiftCode} />: <></>}
            {bank?.branchAddress ? 
            <DetailBlock
              label="Branch Address"
              value={bank?.branchAddress}
              isFullWidth={false}
            />: <></>}
            
            <div className="px-3 py-2 transition-colors absolute top-2 right-2">
              <div className="flex items-center justify-end">
                <button
                  onClick={() =>
                    setIsUpdateBasic({
                      bankName: bank?.bankName ?? "",
                      accountNumber: bank?.accountNumber ?? "",
                      ifscCode: bank?.ifscCode ?? "",
                      swiftCode: bank?.swiftCode ?? "",
                      branchAddress: bank?.branchAddress ?? "",
                      id: selectedClientData?.id ?? "",
                    })
                  }
                  className="text-blue-600"
                >
                  <Edit size={16} />
                </button>
              </div>
            </div>
      </div>}

      {isUpdateBasic !== null && (
        <div
          onClick={() => setIsUpdateBasic(null)}
          className="fixed inset-0 bg-black/40 flex items-center justify-center"
        >
          <BankInfoClient
            handelUpdate={handelUpdate}
            isLoading={isLoading}
            errors={errors}
            formData={isUpdateBasic}
            setFormData={setIsUpdateBasic}
          />
        </div>
      )}
    </div>
  );
};

const DetailBlock = ({ label, value }) => (
  <div className="px-3 py-2 transition-colors">
    <div className="text-[10px] uppercase font-bold text-gray-400 mb-0.5 tracking-tight">
      {label}
    </div>
    <div className="text-sm font-semibold text-gray-800 break-all leading-tight">
      {value || "—"}
    </div>
  </div>
);

export default BankDetails;
